import { useState, useEffect, useCallback } from 'react';
import { TOAST_EVENT, type Toast } from './toast-bus.ts';

let _id = 0;

export function ToastContainer() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const add = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = ++_id;
    setToasts((prev) => [...prev, { id, ...toast }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 6000);
  }, []);

  const remove = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    function handler(e: Event) {
      add((e as CustomEvent<Omit<Toast, 'id'>>).detail);
    }
    window.addEventListener(TOAST_EVENT, handler);
    return () => window.removeEventListener(TOAST_EVENT, handler);
  }, [add]);

  if (toasts.length === 0) return null;

  return (
    <div style={{
      position: 'fixed',
      top: '1.25rem',
      right: '1.25rem',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      pointerEvents: 'none',
      maxWidth: '420px',
      width: 'calc(100vw - 2.5rem)',
    }}>
      {toasts.map((toast) => {
        const isError = toast.kind === 'error';
        return (
          <div
            key={toast.id}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              padding: '12px 14px',
              borderRadius: '12px',
              background: 'var(--color-widget-bg)',
              border: `1px solid ${isError ? 'var(--color-widget-danger)' : 'var(--color-cp-lime)'}`,
              boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
              pointerEvents: 'auto',
              animation: 'dashboardFadeIn 0.2s ease',
            }}
          >
            <span
              className="material-symbols-outlined"
              style={{
                fontSize: '18px',
                color: isError ? 'var(--color-widget-danger)' : 'var(--color-cp-lime)',
                flexShrink: 0,
                marginTop: '1px',
                fontVariationSettings: "'FILL' 1",
              }}
            >
              {isError ? 'error' : 'check_circle'}
            </span>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: isError ? 'var(--color-widget-danger)' : 'var(--color-cp-lime)',
                marginBottom: '3px',
              }}>
                {toast.title}
              </div>
              <div style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '12px',
                color: 'var(--color-widget-text)',
                lineHeight: 1.5,
                wordBreak: 'break-word',
              }}>
                {toast.message}
              </div>
            </div>

            <button
              type="button"
              onClick={() => remove(toast.id)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                color: 'var(--color-widget-text-muted)',
                display: 'flex',
                flexShrink: 0,
              }}
              aria-label="Cerrar"
            >
              <span className="material-symbols-outlined" style={{ fontSize: '15px' }}>close</span>
            </button>
          </div>
        );
      })}
    </div>
  );
}
